import { useContext, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import {
  BacktestResultsSkeleton,
  ChartPane,
  ChartReplayBar,
  ChartView,
  LogsPanel,
  TradeHistoryPanel,
} from '@/components'
import type { Candle, ChartMarker } from '@/components/types/chart'
import BacktestBottomPanel, { type BottomTab } from './BacktestBottomPanel'
import ResizableVerticalPanel from '../../components-legacy/ResizableVerticalPanel'
import { ThemeContext } from '../../providers/ThemeProvider'
import type { BotCode } from '../api'

interface BotBacktestPlaceholderProps {
  currentCode?: BotCode | null
  hasCode?: boolean
  activeBottomTab?: BottomTab
  onActiveBottomTabChange?: (tab: BottomTab) => void
}

const TRADES_EMPTY_LABEL = (
  <>
    Describe your strategy and AI will build and backtest it here.
    <ArrowRight
      size={14}
      strokeWidth={1.5}
      // className="hidden inline-block align-middle ml-1"
      className="hidden min-[1100px]:inline-block align-middle ml-1"
    />
  </>
)

const LOGS_EMPTY_LABEL = (
  <>
    Your strategy logs will live here. 
  </>
)

const CHART_EMPTY_MESSAGE = (
  <p className="text-body-strong text-foreground leading-relaxed">
    Describe your strategy and
    <br />
    AI will build and backtest it here.
    <ArrowRight
      size={14}
      strokeWidth={1.5}
      // className="hidden inline-block align-middle ml-1"
      className="hidden min-[1100px]:inline-block align-middle ml-1"
    />
  </p>
)

// Mulberry32-style seeded RNG so the placeholder candles are deterministic
// across reloads but still look like natural price action.
function createRng(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s + 0x6d2b79f5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function generatePlaceholderOhlc(): Candle[] {
  const rand = createRng(0xc0ffee)
  const candles: Candle[] = []
  const candleCount = 320
  const startTime = Math.floor(Date.UTC(2024, 0, 1) / 1000)
  const stepSeconds = 24 * 60 * 60
  const volatility = 0.85
  const trend = 0.15
  const round = (n: number) => Math.round(n * 100) / 100

  let price = 100
  for (let i = 0; i < candleCount; i++) {
    const time = startTime + i * stepSeconds
    const vol = volatility * (0.6 + rand() * 0.8)
    const dir = rand() > 0.5 - trend * 0.15 ? 1 : -1
    const open = price
    const close = open + dir * vol * (0.4 + rand() * 0.8)
    const high = Math.max(open, close) + rand() * vol * 0.8
    const low = Math.min(open, close) - rand() * vol * 0.8
    candles.push({
      time,
      open: round(open),
      high: round(high),
      low: round(low),
      close: round(close),
    })
    price = close
  }
  return candles
}

function generatePlaceholderMarkers(ohlc: Candle[]): ChartMarker[] {
  if (ohlc.length === 0) return []
  const positions = [0.12, 0.25, 0.4, 0.55, 0.7, 0.86]
  return positions.map((frac, idx) => {
    const i = Math.min(ohlc.length - 1, Math.floor(frac * ohlc.length))
    const isBuy = idx % 2 === 0
    return {
      time: ohlc[i].time,
      position: isBuy ? 'belowBar' : 'aboveBar',
      color: isBuy ? '#3db87a' : '#e05858',
      shape: isBuy ? 'arrowUp' : 'arrowDown',
      text: '',
    }
  })
}

const PLACEHOLDER_OHLC: Candle[] = generatePlaceholderOhlc()
const PLACEHOLDER_MARKERS: ChartMarker[] = generatePlaceholderMarkers(PLACEHOLDER_OHLC)

const NOOP = () => {}

export default function BotBacktestPlaceholder({
  currentCode,
  hasCode = false,
  activeBottomTab,
  onActiveBottomTabChange,
}: BotBacktestPlaceholderProps) {
  const themeCtx = useContext(ThemeContext) as { theme?: string } | undefined
  const isDark = themeCtx?.theme === 'dark'

  const [isBottomMaximized, setIsBottomMaximized] = useState(false)
  const [internalActiveBottomTab, setInternalActiveBottomTab] = useState<BottomTab>('results')
  const resolvedActiveTab = activeBottomTab ?? internalActiveBottomTab
  const setActiveTab = (tab: BottomTab) => {
    if (activeBottomTab === undefined) {
      setInternalActiveBottomTab(tab)
    }
    onActiveBottomTabChange?.(tab)
  }

  const resultsContent = <BacktestResultsSkeleton />

  const tradesContent = (
    <TradeHistoryPanel
      trades={[]}
      isEmpty
      emptyLabel={TRADES_EMPTY_LABEL}
      showSubTabsWhenEmpty={false}
    />
  )

  const logsContent = (
    <LogsPanel logs={[]} isEmpty emptyLabel={LOGS_EMPTY_LABEL} />
  )

  const bottomPanel = (
    <BacktestBottomPanel
      tradesContent={tradesContent}
      newResultsContent={resultsContent}
      logsContent={logsContent}
      currentCode={currentCode}
      hasCode={hasCode}
      activeTab={resolvedActiveTab}
      onActiveTabChange={setActiveTab}
      isMaximized={isBottomMaximized}
      onToggleMaximize={() => setIsBottomMaximized((v) => !v)}
    />
  )

  const chartSlot = (
    <ChartView
      ohlc={PLACEHOLDER_OHLC}
      markers={PLACEHOLDER_MARKERS}
      theme={isDark ? 'dark' : 'light'}
      attributionLogo={false}
      barSpacing={10}
    />
  )

  const replayBarSlot = (
    <ChartReplayBar
      ohlc={PLACEHOLDER_OHLC}
      playing={false}
      speed={1}
      visibleCount={PLACEHOLDER_OHLC.length}
      totalCandles={PLACEHOLDER_OHLC.length}
      backtestRunning={false}
      onRestart={NOOP}
      onTogglePlay={NOOP}
      onCycleSpeed={NOOP}
      onSpeedChange={NOOP}
      onScrub={NOOP}
    />
  )

  const chartPane = (
    <div className="h-full flex flex-col overflow-hidden">
      <ChartPane
        isEmpty
        chartSlot={chartSlot}
        replayBarSlot={replayBarSlot}
        emptyMessage={CHART_EMPTY_MESSAGE}
      />
    </div>
  )

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--base-100)' }}>
      <div className="flex-1 min-h-0">
        {isBottomMaximized ? (
          bottomPanel
        ) : (
          <ResizableVerticalPanel
            defaultTopHeight={55}
            minTopHeight={25}
            maxTopHeight={80}
            storageKey="backtest-chart-split"
          >
            {chartPane}
            {bottomPanel}
          </ResizableVerticalPanel>
        )}
      </div>
    </div>
  )
}
